import { Nullable } from "@shared/models/general";
import { MimeType } from "@shared/models/mimeType";

export type CustomFieldsMap = {
  [key in InvestorRequestType]: {
    [key: string]: string;
  };
};

export type RequiredFieldsPath = {
  [key in InvestorRequestType]: string[];
};

export enum InvestorRequestType {
  General = "information-request-general-support",
  UpdateWireInstructions = "update-wire-instructions",
  NoData = "no-data-request",
}

export type WireFormStep = "chooseMethod" | "fillManually";

// Payment method constants for type-safe comparisons (avoid magic strings)
export enum InvestorRequestPaymentMethod {
  Ach = "ach",
  FedWire = "fedwire",
  Swift = "swift",
}

export interface CommonFields {
  selectedFunds: string[];
  selectedAccounts: string[];
  message: string;
  attachment: Nullable<{
    type: MimeType;
    name: string;
    base64: string;
  }>;
}

export type CustomFields = { [key: string]: any };

export interface Country {
  id: string;
  name: string;
}

// LPC-9775: Session attachment for hybrid users (preserved across tab switches until dialog close)
export interface SessionAttachment {
  attachmentId?: string; // UUID from draft, if loaded
  attachmentName: string;
  attachmentFile: {
    name: string;
    type: string;
    base64: string;
  };
}

export interface InvestorRequestState {
  open: boolean;
  requestType: Nullable<InvestorRequestType>;
  commonFields: CommonFields;
  customFields: CustomFields;
  // Tab Independence: Separate state for Internal and External tabs
  tabSpecificData: {
    internal: {
      commonFields: CommonFields;
      customFields: CustomFields;
      hasBeenInitialized: boolean; // Track if tab has been loaded/edited
      // LPC-9775: Draft attachment metadata per tab (for download functionality)
      draftAttachmentId: Nullable<string>;
      draftAttachmentName: Nullable<string>;
      draftAttachmentURL: Nullable<string>;
      // Track unsaved changes for "Save as Draft" button state
      hasUnsavedChanges: boolean;
      // Snapshot of last saved/loaded draft for comparison
      lastSavedState: Nullable<{
        commonFields: CommonFields;
        customFields: CustomFields;
      }>;
    };
    external: {
      commonFields: CommonFields;
      customFields: CustomFields;
      hasBeenInitialized: boolean; // Track if tab has been loaded/edited
      // LPC-9775: Draft attachment metadata per tab (for download functionality)
      draftAttachmentId: Nullable<string>;
      draftAttachmentName: Nullable<string>;
      draftAttachmentURL: Nullable<string>;
      // Track unsaved changes for "Save as Draft" button state
      hasUnsavedChanges: boolean;
      // Snapshot of last saved/loaded draft for comparison
      lastSavedState: Nullable<{
        commonFields: CommonFields;
        customFields: CustomFields;
      }>;
    };
  };
  retainedCustomFields: CustomFieldsMap;
  loading: boolean;
  validationWarningShown: boolean;
  submitSuccess: boolean;
  wireUnavailable?: boolean;
  activeInfoTab?: string;
  countries: {
    loading: boolean;
    data: Country[];
  };
  draft: {
    loading: boolean;
    attachmentId: Nullable<string>; // UUID string, not number
    attachmentName: Nullable<string>;
    attachmentURL: Nullable<string>;
  };
  // LPC-9775: Session-based attachment storage for hybrid users (Internal/External tabs)
  // Session takes priority over draft - represents user's current unsaved work
  sessionAttachments: {
    internal: Nullable<SessionAttachment>;
    external: Nullable<SessionAttachment>;
  };
  // LPC-10289: C2DS validation errors mapped to form fields
  c2dsErrors: Nullable<C2DSFieldErrors>;
  wireFormStep: Nullable<WireFormStep>;
}

export interface InvestorRequestBody {
  requestType: InvestorRequestType;
  entitledEntities: string[];
  entitledAccounts: string[];
  request: string;
  customFields: CustomFields;
  attachmentName?: string;
  attachmentId?: string; // UUID string for attachments
  attachmentURL?: string; // URL for attachments
}

// Draft-related interfaces for LPC-9775
export interface SaveDraftPayload {
  fundType: string;
  jsonFormData: string; // JSON stringified
  attachmentName?: string;
  attachment?: number[]; // byte array from base64
}

export interface DraftValuesResponse {
  wireInstructionRequestId: number;
  userId: number;
  wireInstructionsId: number;
  fundType: string;
  entitledUser: any;
  wireInstructions: {
    wireInstructionsId: number;
    instructions: string; // JSON stringified form data
    wireTransferInstructionRequests: any[];
  };
}

export interface DraftFormData {
  commonFields: CommonFields;
  customFields: CustomFields;
  attachmentId?: string; // UUID string
  attachmentName?: string;
  AttachmentURL?: string;
}

// Response interface for wire instruction submission
export interface WireInstructionSubmitResponse {
  success: boolean;
  attachmentId?: string;
  attachmentName?: string;
  attachmentURL?: string;
}

// LPC-10289: C2DS validation error types
export type C2DSFieldKey =
  | "routingNumber"
  | "SwiftId"
  | "IBAN"
  | "BankIdNbr"
  | "intermediarySwiftOrAba"
  | "intermediaryRoutingNumber";

export interface C2DSFieldError {
  fieldKey: C2DSFieldKey;
  displayName: string;
  message: string;
}

export type C2DSFieldErrors = Record<C2DSFieldKey, C2DSFieldError | null>;
